import React from "react";
import PropTypes from "prop-types";
import { STATUS_META } from "../../services/LabService";

/** Compact status pill keyed on backend status string. */
export const StatusPill = ({ status, children }) => {
    const meta = STATUS_META[status] || STATUS_META.queued;
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.pill}`}
        >
            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
            {children || meta.label}
        </span>
    );
};
StatusPill.propTypes = {
    status: PropTypes.string.isRequired,
    children: PropTypes.node,
};

/** Stat card used on the dashboard. */
export const StatCard = ({ label, value, delta, hint }) => (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4">
        <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
            {label}
        </div>
        <div className="mt-2 flex items-baseline gap-2">
            <div className="text-2xl font-semibold text-slate-900 dark:text-slate-50">{value}</div>
            {delta ? (
                <span
                    className={`text-xs font-medium ${
                        delta.direction === "down"
                            ? "text-emerald-600 dark:text-emerald-400"
                            : delta.direction === "up"
                                ? "text-red-600 dark:text-red-400"
                                : "text-slate-500"
                    }`}
                >
                    {delta.label}
                </span>
            ) : null}
        </div>
        {hint ? <div className="mt-1 text-xs text-slate-500">{hint}</div> : null}
    </div>
);
StatCard.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.node.isRequired,
    delta: PropTypes.shape({ direction: PropTypes.string, label: PropTypes.string }),
    hint: PropTypes.node,
};

/** Standard panel container. */
export const Panel = ({ title, action, children, padded = true }) => (
    <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
        {(title || action) ? (
            <header className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-800">
                <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-50">{title}</h2>
                {action}
            </header>
        ) : null}
        <div className={padded ? "p-5" : ""}>{children}</div>
    </section>
);
Panel.propTypes = {
    title: PropTypes.node,
    action: PropTypes.node,
    children: PropTypes.node,
    padded: PropTypes.bool,
};

/** Generic empty state. */
export const EmptyState = ({ icon, title, body, action }) => (
    <div className="text-center py-12">
        {icon ? <div className="flex justify-center mb-3 text-slate-400">{icon}</div> : null}
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
        {body ? <p className="mt-1 text-sm text-slate-500 max-w-sm mx-auto">{body}</p> : null}
        {action ? <div className="mt-4">{action}</div> : null}
    </div>
);
EmptyState.propTypes = {
    icon: PropTypes.node,
    title: PropTypes.node.isRequired,
    body: PropTypes.node,
    action: PropTypes.node,
};

export const formatRelative = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.valueOf())) return iso;
    const diffMs = Date.now() - d.valueOf();
    const sec = Math.round(diffMs / 1000);
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
    if (sec < 86_400) return `${Math.round(sec / 3600)}h ago`;
    return d.toLocaleDateString();
};

export const formatDuration = (ms) => {
    if (!ms && ms !== 0) return "—";
    if (ms < 1000) return `${ms}ms`;
    const s = ms / 1000;
    if (s < 60) return `${s.toFixed(1)}s`;
    const m = Math.floor(s / 60);
    const rem = Math.round(s - m * 60);
    return `${m}m ${rem}s`;
};

export const formatUsd = (n) => {
    if (n === null || n === undefined) return "—";
    return `$${Number(n).toFixed(2)}`;
};

export const formatPct = (n) => {
    if (n === null || n === undefined) return "—";
    const sign = n > 0 ? "+" : "";
    return `${sign}${Number(n).toFixed(1)}%`;
};
